import { useCallback } from 'react'

import { type Query, useQuery } from '@tanstack/react-query'

import { useAuthInfo, useOrgHelper } from '@sweetauth/core'

import { API_URL } from '@/constants/env'

import { convertDatesInObject } from '@/helpers/api'
import { SweetspotError } from '@/helpers/error'

import { organizationsQueryKeys } from '@/hooks/organization/types'

import type { Organization } from '@/types/Organization'

export interface UseDefaultOrganizationOptions {
  enabled?: boolean
}

const fetchDefaultOrganization = async (
  accessToken: string | undefined | null,
): Promise<Response | null> => {
  if (accessToken === null || accessToken === undefined) {
    return null
  }
  const res: Response = await fetch(`${API_URL}/api/v1/organizations/default`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  return res
}

export const useDefaultOrganization = ({
  enabled = true,
}: UseDefaultOrganizationOptions = {}) => {
  const { accessToken, loading: authIsLoading } = useAuthInfo()
  const { orgHelper, loading: orgHelperIsLoading } = useOrgHelper()

  const isLoading = authIsLoading || orgHelperIsLoading
  const hookEnabled =
    enabled &&
    !isLoading &&
    !!accessToken &&
    !!orgHelper &&
    orgHelper.getOrgIds().length > 0

  const refetchInterval = useCallback(
    (query: Query<Organization | null>) => {
      if (query.state.data !== undefined) {
        // this will continue to refetch until the user has a default org
        if (
          query.state.data === null &&
          orgHelper &&
          orgHelper.getOrgIds().length > 0
        ) {
          return 2000
        }
        return false
      }
      return 2000
    },
    [orgHelper?.getOrgIds],
  )

  const queryFn = useCallback(async () => {
    const res: Response | null = await fetchDefaultOrganization(accessToken)
    if (res === null || res.status === 404) {
      return null
    }
    if (!res.ok) {
      throw new SweetspotError({
        name: 'FETCH_ERROR',
        message: res.statusText,
        cause: {
          status: res.status,
        },
      })
    }
    return convertDatesInObject(await res.json()) as Organization
  }, [accessToken])

  return useQuery<Organization | null>({
    queryFn,
    queryKey: organizationsQueryKeys.default(),
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: hookEnabled,
    refetchInterval,
  })
}
